/**
 * useOrgManagementPolicy Hook
 * V136: 조직관리 메뉴 직책 레벨 기반 접근 제어
 * V136.1: jobTitleId → master_jobTitles.code 기반 레벨 판별로 변경
 *         (보안 강화: 기본값 MEMBER로 최소 권한 적용)
 * V137.2: system_admin 전체 접근 권한 추가
 * V28: Firestore 직접 조회 → Cloud Function 경유로 전환
 *      - getJobTitlesForAdmin: 직책 목록 조회
 *      - getOrgManagementPolicyForAdmin: 조직관리 정책 조회
 *
 * 기능:
 * 1. Cloud Function으로 orgManagementPolicies/default 정책 조회
 * 2. Cloud Function으로 jobTitles 조회 (직책 코드 기반 레벨 판별)
 * 3. system_admin이면 모든 메뉴 접근 허용 (V137.2)
 * 4. 메뉴별 접근 가능 여부 판정
 *
 * V28 변경사항:
 * - Firestore 직접 조회 제거
 * - getJobTitlesForAdmin, getOrgManagementPolicyForAdmin Cloud Function 호출
 * - Firestore Rules 권한 오류 해결
 */

import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { useAuthorization } from './useAuthorization';
import {
  OrgManagementPolicy,
  OrgManagementMenuKey,
  JobTitleLevel,
  DEFAULT_ORG_MANAGEMENT_POLICY,
  getJobTitleLevelByCode,
  canAccessByLevel,
  PATH_TO_MENU_KEY,
} from '../types/orgManagementPolicy';
import { ADMIN_LIKE_ROLES } from '../../types/role';

/**
 * V28: getJobTitlesForAdmin 응답 타입
 */
interface GetJobTitlesForAdminResponse {
  success: boolean;
  jobTitles: Array<{
    id: string;
    name: string;
    code: string;
    level?: number;
    order?: number;
    description?: string;
    status?: string;
  }>;
  total: number;
}

/**
 * V28: getOrgManagementPolicyForAdmin 응답 타입
 */
interface GetOrgManagementPolicyForAdminResponse {
  success: boolean;
  policy: {
    menuMaxLevel: Record<string, string>;
    description?: string;
    updatedAt?: any;
  } | null;
  exists: boolean;
}

/**
 * jobTitles 캐시 (모듈 레벨)
 * V28: Cloud Function 결과 캐싱 (동일 세션에서 1회만 로드)
 */
let jobTitlesCache: Map<string, string> | null = null; // id → code
let jobTitlesCachePromise: Promise<Map<string, string>> | null = null;

/**
 * V28: Cloud Function으로 jobTitles 조회 (캐싱)
 */
async function fetchJobTitlesMap(): Promise<Map<string, string>> {
  // 이미 캐시된 경우
  if (jobTitlesCache) {
    return jobTitlesCache;
  }

  // 이미 로딩 중인 경우 (중복 요청 방지)
  if (jobTitlesCachePromise) {
    return jobTitlesCachePromise;
  }

  // 새로 로딩
  jobTitlesCachePromise = (async () => {
    const map = new Map<string, string>();

    if (!functions) {
      return map;
    }

    try {
      // V28: Cloud Function 호출
      const getJobTitlesForAdmin = httpsCallable<void, GetJobTitlesForAdminResponse>(
        functions,
        'getJobTitlesForAdmin'
      );

      const result = await getJobTitlesForAdmin();
      const response = result.data;

      if (response.success && response.jobTitles) {
        response.jobTitles.forEach((jt) => {
          if (jt.code) {
            map.set(jt.id, jt.code);
          }
        });
      }
    } catch (err) {
      console.error('[useOrgManagementPolicy] V28 jobTitles 조회 실패:', err);
    }

    jobTitlesCache = map;
    jobTitlesCachePromise = null;
    return map;
  })();

  return jobTitlesCachePromise;
}

export interface UseOrgManagementPolicyResult {
  /** 정책 로딩 상태 */
  isLoading: boolean;

  /** 에러 */
  error: Error | null;

  /** 정책 데이터 */
  policy: OrgManagementPolicy;

  /** 현재 사용자의 직책 레벨 */
  userJobTitleLevel: JobTitleLevel | undefined;

  /** 현재 사용자의 직책명 (users.jobTitle, UI 표시용) */
  userJobTitle: string | undefined;

  /** 현재 사용자의 직책 ID (users.jobTitleId) */
  userJobTitleId: string | undefined;

  /** 현재 사용자의 직책 코드 (master_jobTitles.code) */
  userJobTitleCode: string | undefined;

  /** V137.2: system_admin 여부 */
  isSystemAdmin: boolean;

  /** 특정 메뉴에 접근 가능한지 확인 */
  canAccessMenu: (menuKey: OrgManagementMenuKey) => boolean;

  /** 특정 라우트 경로에 접근 가능한지 확인 */
  canAccessPath: (path: string) => boolean;

  /**
   * 정책 접근 제한으로 인한 차단 여부
   * (RBAC 권한이 있어도 직책 레벨로 차단될 수 있음)
   * V137.2: system_admin이면 항상 false
   */
  isBlockedByPolicy: (path: string) => boolean;
}

/**
 * 조직관리 메뉴 접근 정책 Hook
 *
 * V28: Firestore 직접 조회 → Cloud Function 경유
 */
export function useOrgManagementPolicy(): UseOrgManagementPolicyResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [policy, setPolicy] = useState<OrgManagementPolicy>(DEFAULT_ORG_MANAGEMENT_POLICY);
  const [jobTitlesMap, setJobTitlesMap] = useState<Map<string, string>>(new Map());

  // 현재 사용자 정보 조회
  const { user, hasRole } = useAuthorization();

  // V29.2: 관리자급 역할 여부 확인 (system_admin, role_admin_kr, role_admin_cn)
  // 기존 isSystemAdmin 변수명은 유지하되, 관리자급 역할 전체를 포함하도록 변경
  const isSystemAdmin = useMemo(() => {
    return ADMIN_LIKE_ROLES.some(roleId => hasRole(roleId));
  }, [hasRole]);

  // 사용자의 직책 ID (users.jobTitleId 필드)
  const userJobTitleId = useMemo(() => {
    const profile = user?.profile as any;
    return profile?.jobTitleId as string | undefined;
  }, [user?.profile]);

  // 사용자의 직책명 (UI 표시용, users.jobTitle 필드)
  const userJobTitle = useMemo(() => {
    const profile = user?.profile as any;
    return profile?.jobTitle as string | undefined;
  }, [user?.profile]);

  // 직책 ID → 직책 코드 변환
  const userJobTitleCode = useMemo(() => {
    if (!userJobTitleId) return undefined;
    return jobTitlesMap.get(userJobTitleId);
  }, [userJobTitleId, jobTitlesMap]);

  // V136.1: 직책 코드 → 레벨 변환
  const userJobTitleLevel = useMemo(() => {
    return getJobTitleLevelByCode(userJobTitleCode);
  }, [userJobTitleCode]);

  // V28: Cloud Function으로 정책 + jobTitles 조회
  useEffect(() => {
    // Firebase 미초기화 시 기본 정책 사용
    if (!functions) {
      setPolicy(DEFAULT_ORG_MANAGEMENT_POLICY);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // V28: Cloud Function 호출
        const getOrgManagementPolicyForAdmin = httpsCallable<void, GetOrgManagementPolicyForAdminResponse>(
          functions!,
          'getOrgManagementPolicyForAdmin'
        );

        // 병렬 조회: 정책 + jobTitles
        const [policyResult, jobTitles] = await Promise.all([
          getOrgManagementPolicyForAdmin(),
          fetchJobTitlesMap(),
        ]);

        const policyResponse = policyResult.data;

        // 정책 설정
        if (policyResponse.success && policyResponse.exists && policyResponse.policy) {
          setPolicy(policyResponse.policy as OrgManagementPolicy);
        } else {
          setPolicy(DEFAULT_ORG_MANAGEMENT_POLICY);
        }

        // jobTitles 맵 설정
        setJobTitlesMap(jobTitles);

        setIsLoading(false);
      } catch (err: any) {
        console.error('[useOrgManagementPolicy] V28 데이터 조회 실패:', err);
        setError(err);
        setPolicy(DEFAULT_ORG_MANAGEMENT_POLICY);
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * 특정 메뉴에 접근 가능한지 확인
   *
   * V136.1: 로딩 중에는 보수적으로 차단 (제한 메뉴만)
   * V137.2: system_admin이면 모든 메뉴 접근 허용
   */
  const canAccessMenu = (menuKey: OrgManagementMenuKey): boolean => {
    // V137.2: system_admin이면 모든 메뉴 접근 허용
    if (isSystemAdmin) {
      return true;
    }

    // V136.1: 로딩 중에는 보수적 처리
    // UNLIMITED 메뉴는 허용, 제한 메뉴는 차단
    if (isLoading) {
      const maxLevel = policy.menuMaxLevel[menuKey];
      return maxLevel === 'UNLIMITED';
    }

    const maxLevel = policy.menuMaxLevel[menuKey];
    return canAccessByLevel(userJobTitleLevel, maxLevel);
  };

  /**
   * 특정 라우트 경로에 접근 가능한지 확인
   */
  const canAccessPath = (path: string): boolean => {
    const menuKey = PATH_TO_MENU_KEY[path];
    if (!menuKey) {
      // 조직관리 메뉴가 아니면 정책 적용 안함
      return true;
    }
    return canAccessMenu(menuKey);
  };

  /**
   * 정책 접근 제한으로 인한 차단 여부
   * V137.2: system_admin이면 항상 false (차단 안함)
   */
  const isBlockedByPolicy = (path: string): boolean => {
    // V137.2: system_admin이면 차단 안함
    if (isSystemAdmin) {
      return false;
    }

    const menuKey = PATH_TO_MENU_KEY[path];
    if (!menuKey) {
      // 조직관리 메뉴가 아니면 차단 안함
      return false;
    }
    return !canAccessMenu(menuKey);
  };

  return {
    isLoading,
    error,
    policy,
    userJobTitleLevel,
    userJobTitle,
    userJobTitleId,
    userJobTitleCode,
    isSystemAdmin, // V137.2
    canAccessMenu,
    canAccessPath,
    isBlockedByPolicy,
  };
}

/**
 * ============================================
 * V28 변경 요약
 * ============================================
 *
 * **변경 전:**
 * - Firestore orgManagementPolicies/default 직접 getDoc 호출
 * - Firestore jobTitles 컬렉션 직접 getDocs 호출
 * - Firestore Rules 권한으로 접근 제어
 *
 * **변경 후:**
 * - getOrgManagementPolicyForAdmin Cloud Function 호출
 * - getJobTitlesForAdmin Cloud Function 호출
 * - 서버에서 인증 확인 후 데이터 반환
 *
 * **장점:**
 * - Firestore Rules 권한 오류 해결
 * - 서버에서 인증 확인 강제
 * - SSoT (auth_roleAssignments 기반) 원칙 준수
 *
 * **캐싱:**
 * - jobTitles는 모듈 레벨 캐싱 (동일 세션에서 1회만 로드)
 * - 과도한 Cloud Function 호출 방지
 */
